import React, { useState, createRef } from "react";
import emailjs from "@emailjs/browser";
import ReCAPTCHA from "react-google-recaptcha";
import * as Form from "../style/Form-style";
import * as LPF from "../../components/landingpage/lp-contact-style";

const serviceID = process.env.REACT_APP_EMAILJS_SERVICE_ID!;
const templateID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID!;
const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY!;
const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY!;

export const ContactForm: React.FC = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
    message: "",
    companyName: "",
    extraDetails: "",
    referenceCode: "",
  });

  const [acceptConditions, setAcceptConditions] = useState(false);
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [sendEmailStatus, setSendEmailStatus] = useState("");
  const recaptchaRef = createRef<ReCAPTCHA>();
  const validatePhone = (phone: string) => /^\+?[0-9\s-]{8,15}$/.test(phone);

  const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validateForm = () => {
    const errors: { [key: string]: string } = {};
    if (!formData.fullName) errors.fullName = "* Ditt namn är obligatoriskt";

    if (!formData.phone) {
      errors.phone = "* Telefonnummer är obligatoriskt";
    } else if (!validatePhone(formData.phone)) {
      errors.phone = "Telefonnummer måste vara i ett giltigt format";
    }

    if (!formData.email) {
      errors.email = "* E-post är obligatoriskt";
    } else if (!validateEmail(formData.email)) {
      errors.email = "E-post måste vara i ett giltigt format (t.ex. example@example.com)";
    }

    if (!formData.message) {
      errors.message = "* Meddelande är obligatoriskt";
    } else if (formData.message.length < 10) {
      errors.message = "* Meddelande måste vara minst 10 tecken";
    }

    if (!acceptConditions)
      errors.acceptConditions = "* Du måste acceptera villkoren för att fortsätta.";
    return errors;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAcceptConditions(e.target.checked);
  };

  const handleSubmit = (token: string | null) => {
    const isDetailsSubmitted = formData.extraDetails || formData.referenceCode;
    if (isDetailsSubmitted) return;

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const combinedFormData = {
      ...formData,
      "g-recaptcha-response": token,
    };

    emailjs.send(serviceID, templateID, combinedFormData, { publicKey }).then(
      () => {
        console.log("Email sent successfully");
        setFormErrors({});
        setSendEmailStatus("OK");
        setFormData({
          fullName: "",
          phone: "",
          email: "",
          message: "",
          companyName: "",
          extraDetails: "",
          referenceCode: "",
        });
        setAcceptConditions(false);
        recaptchaRef.current?.reset();
      },
      (error) => {
        console.error("Failed to send email:", error);
        setSendEmailStatus("FAIL");
        recaptchaRef.current?.reset();
      },
    );
  }; // End of handleSubmit function

  const sendEmail = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    recaptchaRef.current?.execute();
  };

  return (
    <Form.Form onSubmit={sendEmail} id="contact-form" noValidate>
      <Form.FormInputContainer>
        <Form.FormInputContacts
          type="text"
          id="fullName"
          name="fullName"
          value={formData.fullName}
          onChange={handleChange}
          aria-label="För & efternamn, obligatoriskt fält"
          autoComplete="name"
          placeholder="För & efternamn*"
        />
        {formErrors.fullName && <LPF.LpErrorMsg>{formErrors.fullName}</LPF.LpErrorMsg>}
      </Form.FormInputContainer>

      <Form.FormInputContainer>
        <Form.FormInputContacts
          type="tel"
          id="phone"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          aria-label="Telefonnummer, obligatoriskt fält"
          autoComplete="tel"
          placeholder="Telefonnummer*"
        />
        {formErrors.phone && <LPF.LpErrorMsg>{formErrors.phone}</LPF.LpErrorMsg>}
      </Form.FormInputContainer>

      <Form.FormInputContainer>
        <Form.FormInputContacts
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          aria-label="E-post, obligatoriskt fält"
          autoComplete="email"
          placeholder="E-mail*"
        />
        {formErrors.email && <LPF.LpErrorMsg>{formErrors.email}</LPF.LpErrorMsg>}
      </Form.FormInputContainer>
      <Form.FormInputContainer>
        <Form.TextArea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          aria-label="Meddelande, obligatoriskt fält"
          placeholder="Skriv ditt meddelande här*"
        />
        {formErrors.message && <LPF.LpErrorMsg>{formErrors.message}</LPF.LpErrorMsg>}
      </Form.FormInputContainer>

      <LPF.LpCheckboxContainer>
        <Form.FormCheckboxInput
          type="checkbox"
          id="acceptConditions"
          name="acceptConditions"
          checked={acceptConditions}
          onChange={handleCheckboxChange}
          aria-label="Jag accepterar villkoren"
        />
        <LPF.LpCheckboxLabel htmlFor="acceptConditions">
          Jag accepterar <LPF.VillkorLink to="/privacy">villkoren</LPF.VillkorLink>*
        </LPF.LpCheckboxLabel>
      </LPF.LpCheckboxContainer>

      {formErrors.acceptConditions && (
        <LPF.LpErrorMsg>{formErrors.acceptConditions}</LPF.LpErrorMsg>
      )}
      <ReCAPTCHA
        ref={recaptchaRef}
        sitekey={recaptchaSiteKey}
        size="invisible"
        onChange={handleSubmit}
      />
      <Form.SendButton type="submit">Skicka</Form.SendButton>

      {sendEmailStatus === "OK" && (
        <LPF.SendMsgText>Tack för din förfrågan, vi kontaktar dig inom kort!</LPF.SendMsgText>
      )}
      {sendEmailStatus === "FAIL" && (
        <LPF.ErrorMsgText>
          Tyvärr gick inte meddelandet att skicka, testa igen eller kontakta oss på info@andtech.se
        </LPF.ErrorMsgText>
      )}
    </Form.Form>
  );
};
